import {
  OrbitControls,
  Environment,
  useProgress,
  Html,
} from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import cn from "classnames";
import Image from "next/image";
import { Suspense, useState, useEffect, useCallback } from "react";

import Degree360Svg from "@images/svg/360-degree.svg";
import { isLocalhost } from "@utils/utils";

import ModelContent from "./ModelContent";
import s from "./ThreeDModel.module.scss";

function LoadingScreen() {
  const { progress } = useProgress();
  return (
    <Html center>
      <div className={s.loadingScreen}>
        <div className={s.loadingText}>Loading: {progress.toFixed(0)}%</div>
      </div>
    </Html>
  );
}

function Scene({ modelSrc, onLoad }) {
  const [isAltPressed, setIsAltPressed] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.altKey || e.ctrlKey) {
        setIsAltPressed(true);
      }
    };

    const handleKeyUp = (e) => {
      if (!e.altKey && !e.ctrlKey) {
        setIsAltPressed(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [modelSrc]);

  return (
    <>
      <ambientLight intensity={4.7} />
      <directionalLight position={[5, 5, 5]} intensity={1.2} castShadow />
      <directionalLight position={[-5, 5, -5]} intensity={0.5} castShadow />
      <ModelContent url={modelSrc} onLoad={onLoad} />
      <OrbitControls
        enableZoom={isAltPressed}
        enablePan={false}
        minPolarAngle={Math.PI / 2}
        maxPolarAngle={Math.PI / 2}
        minDistance={7}
        maxDistance={10}
        zoomSpeed={0.35}
      />
    </>
  );
}

const ThreeDModel = ({ className, modelSrc }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [showLoader, setShowLoader] = useState(true);

  const handleCanvasCreated = useCallback(() => {
    // Use requestAnimationFrame to prevent UI blocking
    requestAnimationFrame(() => {
      setIsLoading(false);
    });
  }, []);

  const handleModelLoad = useCallback(() => {
    // Wait for 0.5-1 second before hiding loader
    const delay = Math.random() * 500 + 500;
    setTimeout(() => {
      setShowLoader(false);
    }, delay);
  }, []);

  useEffect(() => {
    // Reset loader state when model source changes
    setShowLoader(true);
  }, [modelSrc]);

  // THIS line fix cors error only for localhost development
  const modelUrlWithEnvironment = isLocalhost()
    ? modelSrc.replace(".cdn", "")
    : modelSrc;

  return (
    <div
      className={cn(s.canvasWrapper, className)}
      style={{ width: "100%", height: "100%" }}
    >
      <Image src={Degree360Svg} alt="360 degree" className={s.degree360} />
      <Canvas
        camera={{ position: [0, 0, 0], fov: 40 }}
        style={{ background: "transparent" }}
        gl={{
          antialias: true,
          alpha: true,
          preserveDrawingBuffer: true,
          powerPreference: "high-performance",
          stencil: false,
          depth: true,
        }}
        dpr={[1, 2]}
        performance={{ min: 0.5 }}
        onCreated={handleCanvasCreated}
      >
        <Suspense fallback={null}>
          <Scene modelSrc={modelUrlWithEnvironment} onLoad={handleModelLoad} />
        </Suspense>

        <Environment preset="city" />
      </Canvas>
    </div>
  );
};

export default ThreeDModel;
